import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, Settings, Zap, Rocket, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HOWITWORKS_IMAGE = '/assets/How-it-works.webp';

const HowItWorksPreview = () => {
  const steps = [
    { number: '01', icon: Download, title: 'Install AI', desc: 'One-click from Shopify App Store', color: 'bg-teal-50 border-teal-200', iconBg: 'bg-teal-500' },
    { number: '02', icon: Settings, title: 'Train Model', desc: 'AI learns your product catalog', color: 'bg-cyan-50 border-cyan-200', iconBg: 'bg-cyan-500' },
    { number: '03', icon: Zap, title: 'Activate Agent', desc: 'Configure & go live in minutes', color: 'bg-emerald-50 border-emerald-200', iconBg: 'bg-emerald-500' },
    { number: '04', icon: Rocket, title: 'Scale Revenue', desc: 'AI sells for you 24/7', color: 'bg-green-50 border-green-200', iconBg: 'bg-green-500' }
  ];

  return (
    <section className="py-16 sm:py-24 md:py-32 bg-slate-50" data-testid="how-it-works-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6 shadow-sm">
            <CheckCircle className="w-4 h-4 text-teal-600" />
            <span className="text-xs sm:text-sm font-bold text-slate-700">Simple Deployment</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4 sm:mb-5">
            Deploy AI in Minutes
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            No coding required. Install and let AI start converting visitors instantly.
          </p>
        </motion.div>

        {/* Two-column: Steps + Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-8 sm:mb-12">
          {/* Steps */}
          <div className="space-y-3 sm:space-y-4">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className={`bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border-2 ${step.color} shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-4 sm:gap-5`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-3xl sm:text-4xl font-extrabold text-slate-200 flex-shrink-0 w-10">{step.number}</span>
                <div className={`w-11 sm:w-13 h-11 sm:h-13 rounded-xl ${step.iconBg} flex items-center justify-center shadow-lg flex-shrink-0`}>
                  <step.icon className="w-5 sm:w-6 h-5 sm:h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm sm:text-base font-bold text-slate-900">{step.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-500">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img 
              src={HOWITWORKS_IMAGE}
              alt="E-commerce mobile app with AI-powered product recommendations"
              className="rounded-2xl sm:rounded-3xl shadow-xl w-full h-[280px] sm:h-[380px] lg:h-[400px] object-cover border-2 border-slate-200"
              data-testid="how-it-works-image"
            />
            <motion.div 
              className="absolute -bottom-4 -left-3 sm:-left-5 bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-slate-200"
              animate={{ y: [0, -5, 0] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-bold text-slate-900">Setup: Under 5 min</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Link to="/how-it-works">
            <Button 
              size="lg" 
              variant="outline" 
              className="group border-2 border-slate-300 text-slate-700 hover:text-teal-600 hover:border-teal-500 hover:bg-teal-50 font-semibold text-sm sm:text-base px-6 sm:px-8"
              data-testid="learn-more-btn"
            >
              Learn More
              <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksPreview;
